import { CheckCircle2, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';

import { formulaToSegments, type AtomBalance } from './chemicalEquation';
import { formatGrams, type MassCase, type MassModelResult } from './conservationOfMass';

/** Formula with chemically correct subscripts, for example CO₂. */
export function Formula({ formula }: { formula: string }) {
  return (
    <span className="whitespace-nowrap">
      {formulaToSegments(formula).map((segment, index) =>
        segment.subscript ? <sub key={index}>{segment.text}</sub> : <span key={index}>{segment.text}</span>,
      )}
    </span>
  );
}

export function FeedbackPanel({ correct, title, children }: { correct: boolean; title: string; children: ReactNode }) {
  return (
    <div
      role="status"
      className={`mt-5 rounded-[1.5rem] border p-5 ${
        correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'
      }`}
    >
      <div className="flex items-start gap-3">
        {correct ? (
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
        ) : (
          <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-extrabold text-ink">{title}</p>
          <div className="mt-2 space-y-2 font-medium leading-7 text-slate2">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function AtomTally({ atoms }: { atoms: AtomBalance[] }) {
  return (
    <table className="mt-3 w-full text-left text-sm">
      <caption className="sr-only">Atom count on each side of the equation</caption>
      <thead>
        <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
          <th className="py-1 pr-2">Element</th>
          <th className="py-1 pr-2">Reactants</th>
          <th className="py-1 pr-2">Products</th>
          <th className="py-1">Match</th>
        </tr>
      </thead>
      <tbody>
        {atoms.map((atom) => (
          <tr key={atom.element} className="border-t border-hairline">
            <td className="py-2 pr-2 font-extrabold text-ink">{atom.element}</td>
            <td className="py-2 pr-2 font-extrabold text-ink">{atom.left}</td>
            <td className="py-2 pr-2 font-extrabold text-ink">{atom.right}</td>
            <td className={`py-2 font-extrabold ${atom.balanced ? 'text-ink' : 'text-brand-coral'}`}>
              {atom.balanced ? '✓ equal' : '✗ different'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------------------------
// Balance scene, drawn from the case and the model
// ---------------------------------------------------------------------------------------------

const SCENE = { width: 320, height: 230 } as const;
const VESSEL = { left: 110, right: 210, top: 70, bottom: 150 } as const;
const ARROW_SPACING = 34;

export function BalanceScene({
  entry,
  model,
  revealed,
  readingG,
}: {
  entry: MassCase;
  model: MassModelResult;
  revealed: boolean;
  /** Explore passes the reading part-way through the reaction; Practice shows before or after. */
  readingG?: number;
}) {
  const reading = readingG ?? (revealed ? model.afterG : model.beforeG);
  const closed = entry.system === 'closed';
  const crossings = revealed ? model.crossings : [];
  const trapped = revealed && closed && entry.gases.length > 0;
  const firstArrowX = (SCENE.width - (crossings.length - 1) * ARROW_SPACING) / 2;
  const label = [
    `${closed ? 'Sealed' : 'Open'} container on a balance reading ${formatGrams(reading)}`,
    revealed ? 'after the reaction' : 'before the reaction',
    ...crossings.map(
      (crossing) =>
        `${crossing.gas.name} ${crossing.gas.direction === 'in' ? 'enters from the air' : 'leaves to the air'}: ${formatGrams(crossing.massG)}`,
    ),
    trapped ? 'gas stays trapped inside' : '',
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <svg viewBox={`0 0 ${SCENE.width} ${SCENE.height}`} className="mx-auto w-full max-w-md" role="img" aria-label={label}>
      <rect x="40" y="150" width="240" height="22" rx="6" className="fill-surface stroke-ink/40" strokeWidth="2" />
      <rect x="70" y="172" width="180" height="40" rx="8" className="fill-ink" />
      <rect x="110" y="180" width="100" height="24" rx="4" className="fill-wash-mint" />
      <text x="160" y="197" textAnchor="middle" className="fill-ink text-[14px] font-extrabold">
        {formatGrams(reading)}
      </text>
      <path
        d={`M${VESSEL.left} ${VESSEL.top} V${VESSEL.bottom - 8} q0 8 8 8 H${VESSEL.right - 8} q8 0 8 -8 V${VESSEL.top}`}
        className="fill-wash-sky stroke-ink/50"
        strokeWidth="2.5"
      />
      {closed ? (
        <rect x={VESSEL.left - 6} y={VESSEL.top - 14} width={VESSEL.right - VESSEL.left + 12} height="16" rx="4" className="fill-brand-coral/40 stroke-ink/40" />
      ) : null}
      <text x="160" y={VESSEL.top + 48} textAnchor="middle" className="fill-slate2 text-[11px] font-bold">
        {closed ? 'sealed' : 'open'}
      </text>
      {trapped
        ? [0, 1, 2, 3, 4].map((index) => (
            <circle key={index} cx={VESSEL.left + 18 + index * 16} cy={VESSEL.top + 14 + (index % 2) * 10} r="5" className="fill-white stroke-brand-coral" strokeWidth="2" />
          ))
        : null}
      {crossings.map((crossing, index) => {
        const x = firstArrowX + index * ARROW_SPACING;
        const goingOut = crossing.gas.direction === 'out';
        const y1 = goingOut ? 58 : 14;
        const y2 = goingOut ? 14 : 58;
        const head = goingOut ? `M${x - 6} 22 L${x} 12 L${x + 6} 22` : `M${x - 6} 50 L${x} 60 L${x + 6} 50`;
        return (
          <g key={`${crossing.gas.formula}-${crossing.gas.direction}`}>
            <line x1={x} y1={y1} x2={x} y2={y2} className={goingOut ? 'stroke-brand-coral' : 'stroke-brand-sky'} strokeWidth="3" strokeDasharray={goingOut ? '5 3' : undefined} />
            <path d={head} className={`fill-none ${goingOut ? 'stroke-brand-coral' : 'stroke-brand-sky'}`} strokeWidth="3" />
            <text x={x + 9} y={36} className="fill-ink text-[11px] font-extrabold">
              {crossing.gas.formula.replace(/\d/g, (digit) => '₀₁₂₃₄₅₆₇₈₉'[Number(digit)])}
              {goingOut ? ' out' : ' in'}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function MassLedger({ model }: { model: MassModelResult }) {
  const rows = (side: 'reactant' | 'product') => model.species.filter((item) => item.side === side);
  const column = (side: 'reactant' | 'product', title: string, total: number) => (
    <div className="rounded-2xl bg-white/75 p-4">
      <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">{title}</p>
      <ul className="mt-2 space-y-1 text-sm font-bold text-ink">
        {rows(side).map((item) => (
          <li key={`${side}-${item.formula}`} className="flex justify-between gap-3">
            <span>
              <Formula formula={item.formula} />
              {item.gas ? <span className="text-xs text-slate2"> (gas)</span> : null}
            </span>
            <span>{formatGrams(item.massG)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 flex justify-between border-t border-hairline pt-2 text-sm font-extrabold text-ink">
        <span>Total</span>
        <span>{formatGrams(total)}</span>
      </p>
    </div>
  );
  return (
    <div className="mt-4 grid gap-3 sm:grid-cols-2" aria-label="Mass of each substance that reacts or forms">
      {column('reactant', 'Reacting', model.reactantTotalG)}
      {column('product', 'Formed', model.productTotalG)}
    </div>
  );
}
